import React from "react";
import StackTrace from "stacktrace-js";

export interface DevErrorProviderProps {
  children: React.ReactNode;
  urlScheme?: string;
}

export class DevErrorProvider extends React.Component<DevErrorProviderProps> {
  state = { error: null, stackFrames: [] };

  async componentDidCatch(error) {
    const stackFrames = await StackTrace.fromError(error);
    if (stackFrames.some((f) => this.isInternalPath(f.fileName)))
      this.setState({ error, stackFrames });
    else throw error;
  }

  isInternalPath = (filePath) => {
    return filePath.includes("/packages/elements/src/");
  };

  renderStackLink = (frame, urlScheme, index) => {
    const { fileName, lineNumber, columnNumber, functionName } = frame;
    if (!fileName) return null;

    const cleanedPath = fileName.replace(/^.*@fs/, "");
    const isInternal = this.isInternalPath(cleanedPath);
    const url = urlScheme
      .replace("{file}", cleanedPath)
      .replace("{line}", lineNumber)
      .replace("{column}", columnNumber);

    return (
      <div key={index} className="text-sm mb-1">
        {isInternal ? (
          <a
            href={url}
            className="text-blue-600 underline hover:text-blue-800 transition"
          >
            {functionName || "(anonymous)"} — {cleanedPath}:{lineNumber}:
            {columnNumber}
          </a>
        ) : (
          <span className="text-gray-500">
            {functionName || "(anonymous)"} — {cleanedPath}:{lineNumber}:
            {columnNumber}
          </span>
        )}
      </div>
    );
  };

  render() {
    if (process.env.NODE_ENV === "development") return this.props.children;

    const { error, stackFrames } = this.state;
    if (!error) return this.props.children;

    const urlScheme =
      this.props.urlScheme || "vscode://file/{file}:{line}:{column}";

    return (
      <div className="bg-red-50 border border-red-400 text-red-800 rounded-xl p-6 m-4 font-mono shadow-sm">
        <h2 className="text-lg font-semibold mb-2 flex items-center">
          <span className="mr-2 text-4xl -mt-2">⚠</span> An error occurred
        </h2>
        <p className="text-base mb-6">{error.message}</p>
        <div>
          <h3 className="text-sm font-bold text-red-600 mb-2">Stack trace:</h3>
          {stackFrames.map((frame, index) =>
            this.renderStackLink(frame, urlScheme, index)
          )}
        </div>
      </div>
    );
  }
}
